import { makeAutoObservable } from 'mobx';
import { et, ru, enGB } from 'telia-front-react/locale';

import CookieApi from '../util/CookieApi';
import { safeReaction } from '../util/MobxUtil';
import { urlWithLang } from '../util/NavigationUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';

export type Language = 'et' | 'ru' | 'en';
export const DEFAULT_LANGUAGE: Language = 'et';

export const languageSlug = (lang: Language) => {
  return !lang || lang === DEFAULT_LANGUAGE ? '' : `/${lang}`;
};

export const ALLOWED_LANGUAGES: string[] = ['et', 'ru', 'en'];
const LANGUAGE_TO_ALPHA3_MAP: { [language: string]: string } = {
  et: 'EST',
  ru: 'RUS',
  en: 'ENG',
};
export const LOCALE_COOKIE_NAME = 'LANGUAGE_EXT';

export const langForCookieLocale = (locale: string): Language => {
  switch (locale) {
    case 'et_EE':
      return 'et';
    case 'ru_RU':
      return 'ru';
    case 'en_GB':
      return 'en';
    default:
      return null;
  }
};

export const cookieLocaleForLang = (lang: Language): string => {
  switch (lang) {
    case 'et':
      return 'et_EE';
    case 'ru':
      return 'ru_RU';
    case 'en':
      return 'en_GB';
    default:
      return '';
  }
};

export default class LanguageStore {
  private routeStore: RouteStore;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.routeStore = rootStore.routeStore;
    makeAutoObservable(this, undefined, { autoBind: true });
    safeReaction(rootStore, () => this.currentLanguage, this.onCurrentLanguageChanged, { fireImmediately: true });
  }

  get currentLanguage() {
    return this.routeStore.langFromCurrentPath || DEFAULT_LANGUAGE;
  }

  get currentLocale() {
    const currentLanguage = this.currentLanguage;
    switch (currentLanguage) {
      case 'en':
        return enGB;
      case 'ru':
        return ru;
      default:
        return et;
    }
  }

  get currentLanguageAlpha3Code() {
    return LANGUAGE_TO_ALPHA3_MAP[this.routeStore.langFromCurrentPath || DEFAULT_LANGUAGE];
  }

  get initialLanguageChanged() {
    return this.currentLanguage !== this.rootStore.initialState.currentLang;
  }

  get isCurrentLanguageDefault() {
    return this.currentLanguage === DEFAULT_LANGUAGE;
  }

  public urlWithCurrentLang = (url: string) => {
    return urlWithLang(url, this.currentLanguage);
  };

  private onCurrentLanguageChanged() {
    document.querySelector('html').setAttribute('lang', this.currentLanguage);
    this.updateLocaleCookie();
    this.updateCookiebotCulture();
  }

  private updateLocaleCookie() {
    if (this.rootStore.isNode || langForCookieLocale(CookieApi.getCookieValue(LOCALE_COOKIE_NAME)) === this.currentLanguage) {
      return;
    }

    CookieApi.updateCookie(LOCALE_COOKIE_NAME, cookieLocaleForLang(this.currentLanguage));
  }

  private updateCookiebotCulture() {
    const cookiebotScript = document.querySelector<HTMLScriptElement>('#Cookiebot');
    if (!cookiebotScript) {
      return;
    }

    cookiebotScript.dataset.culture = this.currentLanguage;
  }
}
